var _excluded = ["children", "dismissOnBackdropClick", "firstFocusableElement", "isOpen", "onCancel"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { MODAL_BODY_CLASSNAME, MODAL_BODY_ID } from "./ModalBody.js";
import { MODAL_FOOTER_CLASSNAME } from "./ModalFooter.js";
import { MODAL_HEADER_CLASSNAME, MODAL_HEADER_ID } from "./ModalHeader.js";
var FOCUSABLE_ELEMENTS = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, *[tabindex], *[contenteditable]';
var returnFocusToActivator = activator => {
  if (typeof (activator === null || activator === void 0 ? void 0 : activator.focus) === 'function') {
    activator.focus();
  }
};
var setFocus = (modalRef, firstElementRef) => {
  var _ref, _body$querySelector;
  if (firstElementRef !== null && firstElementRef !== void 0 && firstElementRef.current) {
    firstElementRef.current.focus();
    return;
  }
  if (!modalRef.current) {
    return;
  }
  var body = modalRef.current.querySelector(".".concat(MODAL_BODY_CLASSNAME));
  var footer = modalRef.current.querySelector(".".concat(MODAL_FOOTER_CLASSNAME));
  var header = modalRef.current.querySelector(".".concat(MODAL_HEADER_CLASSNAME));
  var firstFocusableElement = (_ref = (_body$querySelector = body === null || body === void 0 ? void 0 : body.querySelector(FOCUSABLE_ELEMENTS)) !== null && _body$querySelector !== void 0 ? _body$querySelector : footer === null || footer === void 0 ? void 0 : footer.querySelector(FOCUSABLE_ELEMENTS)) !== null && _ref !== void 0 ? _ref : header === null || header === void 0 ? void 0 : header.querySelector(FOCUSABLE_ELEMENTS);
  firstFocusableElement === null || firstFocusableElement === void 0 ? void 0 : firstFocusableElement.focus();
};
var trapFocus = (event, modalRef) => {
  if (!modalRef.current || event.key !== 'Tab') return;
  var focusable = modalRef.current.querySelectorAll(FOCUSABLE_ELEMENTS);
  var firstFocusable = focusable[0];
  var lastFocusable = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === firstFocusable) {
    event.preventDefault();
    lastFocusable.focus();
  }
  if (!event.shiftKey && document.activeElement === lastFocusable) {
    event.preventDefault();
    firstFocusable.focus();
  }
};
var closeOnEscape = (event, close) => {
  if (event.key === 'Escape') close();
};
var Wrapper = styled.div.withConfig({
  componentId: "CL__sc-1orbdlh-0"
})(["position:fixed;top:0;right:0;bottom:0;left:0;z-index:9991;height:100vh;width:100vw;"]);
var Backdrop = motion(styled.div.withConfig({
  componentId: "CL__sc-1orbdlh-1"
})(["position:fixed;height:100%;width:100%;background-color:var(--black);"]));
var StyledContainer = styled(motion.div).withConfig({
  componentId: "CL__sc-1orbdlh-2"
})(["position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:608px;overflow:auto;max-height:calc(100% - var(--spacing-2));max-width:calc(100% - var(--spacing-2));padding:var(--spacing-5) var(--spacing-6) var(--spacing-7);background-color:var(--modal-bkg-color);border-radius:var(--border-radius-large);box-shadow:var(--box-shadow-small);"]);
var Modal = _ref2 => {
  var {
      children,
      dismissOnBackdropClick = true,
      firstFocusableElement,
      isOpen,
      onCancel
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded);
  var modalRef = useRef(null);
  var [activator, setActivator] = useState(null);
  useEffect(() => {
    if (!isOpen) {
      returnFocusToActivator(activator);
      return;
    }
    setActivator(document.activeElement);
    setFocus(modalRef, firstFocusableElement);
    var eventHandlers = event => {
      trapFocus(event, modalRef);
      closeOnEscape(event, onCancel);
    };
    window.addEventListener('keydown', eventHandlers);
    return () => {
      window.removeEventListener('keydown', eventHandlers);
    };
  }, [isOpen]);
  return /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(AnimatePresence, null, isOpen && /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Backdrop, {
    key: "backdrop",
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 'var(--modal-backdrop-opacity)'
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    },
    onClick: dismissOnBackdropClick ? onCancel : undefined
  }), /*#__PURE__*/React.createElement(StyledContainer, _extends({
    key: "modal-container",
    initial: {
      x: '-50%',
      y: '-45%',
      opacity: 0
    },
    animate: {
      x: '-50%',
      y: '-50%',
      opacity: 1
    },
    exit: {
      x: '-50%',
      y: '-45%',
      opacity: 0
    },
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    },
    ref: modalRef,
    "aria-labelledby": MODAL_HEADER_ID,
    "aria-describedby": MODAL_BODY_ID,
    "aria-modal": "true",
    role: "dialog"
  }, props), children))), document.body);
};
export default Modal;